import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getToken } from "../../features/user/UserAvatar";
import { error, failedResponse, getBackendUrl, loaded, loading, LoadState, LoadStateInfo, notStarted } from "../common";
import { InviteResponse } from "../user/createInviteSlice";

export interface ClassroomStudentInvitesState {
    state: LoadStateInfo;
    request?: RetrieveStudentInviteRequest;
    response?: InviteResponse;
}

const initialState: ClassroomStudentInvitesState = {
    state: notStarted(),
};

export interface RetrieveStudentInviteRequest {
    studentId: string;
}

export const retrieveStudentInvite = createAsyncThunk(
    "retrieveStudentInvite",
    async (params: RetrieveStudentInviteRequest, thunkAPI) => {
        console.debug(`retrieve student invites for student ${params.studentId}`);
        const response = await fetch(`${getBackendUrl()}/api/student/${params.studentId}/student-invite`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });
        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveStudentInviteRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.studentInvite;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveStudentInvite skipped: ${status.state.state}`);
            return false;
        }
    }
);

export const studentInviteSlice = createSlice({
    name: "studentInvite",
    initialState,
    reducers: {
        clean: (state) => {
            state.state = notStarted();
            state.response = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveStudentInvite.pending, (state, action) => {
                state.state = loading();
                state.response = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveStudentInvite.fulfilled, (state, action) => {
                state.state = loaded();
                state.response = action.payload;
            })
            .addCase(retrieveStudentInvite.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export const selectStudentInvite = (state: RootState) => state.studentInvite;

export default studentInviteSlice.reducer;
